import type { ConversationState } from '@takeit4me/shared'

/**
 * Conversation states (design 5.4) and the moves allowed between them. Every state change in the API goes
 * through services/conversationState.ts, which checks this table (A 5.4-08).
 * - resolved → waiting_customer: we sent something new on a resolved conversation (an invoice, a staff message).
 * - with_ai → closed: the only message was an automatic message or spam, so there was never a conversation.
 * - closed is final; only a recorded correction (a person says "not spam", an erasure) moves it.
 */
const NEXT: Record<ConversationState, ConversationState[]> = {
  with_ai: ['with_ai', 'clarifying', 'waiting_customer', 'waiting_person', 'resolved', 'closed'],
  clarifying: ['clarifying', 'with_ai', 'waiting_customer', 'waiting_person', 'resolved'],
  waiting_customer: ['with_ai', 'clarifying', 'waiting_person', 'resolved'],
  waiting_person: ['with_ai', 'waiting_customer', 'waiting_person', 'resolved', 'clarifying'],
  resolved: ['with_ai', 'clarifying', 'waiting_person', 'waiting_customer', 'closed'],
  closed: [],
}
export const canTransition = (a: ConversationState, b: ConversationState) => NEXT[a].includes(b)
/** The states a conversation may move to `to` from (staying in the same state is always allowed). */
export const allowedFrom = (to: ConversationState): ConversationState[] =>
  (Object.keys(NEXT) as ConversationState[]).filter((a) => a === to || NEXT[a].includes(to))

/**
 * Same topic? (design 5.4: a resolved conversation "reopens if the customer writes again about the same topic").
 * A cheap, deterministic check with no model call: shared reference numbers, the email subject, and the content
 * words the message shares with the conversation's subject, summary and request type. It fails safe: when it
 * cannot tell (a short message such as "thanks" or "one more thing"), the answer is `unsure`, and the resolved
 * conversation is reopened, so the customer's history stays together and the resolution rate is not flattered.
 */
export type TopicMatch = 'same' | 'different' | 'unsure'
export interface TopicOf { subject: string | null; summary?: string | null; request_type?: string | null }

const STOP = new Set(('about above after again also always another anything around because been before being below between both cannot could does doing done '
  + 'down during each every from have having here hers herself himself into itself just like made make many more most much must myself need never once only '
  + 'other ours over same should some such than that their theirs them then there these they this those through very want were what when where which while '
  + 'with would your yours yourself thanks thank please hello dear regards cheers kind best wishes today tomorrow week know sure still yeah okay will shall '
  + 'going thing things time times something someone there question questions help message email reply wrote sent asking general').split(/\s+/))
const REF = /\b(?:[a-z]{1,6}-\d{3,}|\d{5,})\b/gi
const stem = (w: string) => w.replace(/(?:ing|ed|es|s)$/, '')
function contentWords(text: string): Set<string> {
  const out = new Set<string>()
  for (const w of text.toLowerCase().replace(REF, ' ').split(/[^a-zÀ-ɏ]+/)) if (w.length >= 4 && !STOP.has(w)) out.add(stem(w))
  return out
}
const refsIn = (text: string) => new Set([...text.matchAll(REF)].map((m) => m[0].toLowerCase()))
const bareSubject = (s: string | null | undefined) => String(s ?? '').replace(/^\s*((re|fw|fwd|aw|tr|sv)\s*(\[\d+\])?\s*:\s*)+/i, '').replace(/\s+/g, ' ').trim().toLowerCase()

export function topicMatch(message: { subject?: string | null; text: string }, conv: TopicOf): TopicMatch {
  const msgText = `${message.subject ?? ''}\n${message.text ?? ''}`
  const convText = `${conv.subject ?? ''}\n${conv.summary ?? ''}\n${String(conv.request_type ?? '').replace(/_/g, ' ')}`
  const mRefs = refsIn(msgText), cRefs = refsIn(convText)
  if ([...mRefs].some((r) => cRefs.has(r))) return 'same'
  const ms = bareSubject(message.subject), cs = bareSubject(conv.subject)
  if (ms && cs && ms === cs) return 'same'
  const mw = contentWords(msgText), cw = contentWords(convText)
  const shared = [...mw].filter((w) => cw.has(w)).length
  if (shared >= 2 || (shared >= 1 && mw.size <= 4)) return 'same'
  // Different only when both sides say enough to judge and share nothing (or name different references).
  if (mRefs.size && cRefs.size) return 'different'
  if (shared === 0 && mw.size >= 4 && cw.size >= 3) return 'different'
  return 'unsure'
}

/**
 * When a customer writes, which conversation does it belong to? An open one, or a resolved one inside the reopen
 * window when the message is about the same topic (or the check cannot tell); otherwise a new conversation.
 * Email threading headers win when present.
 */
export interface OpenConversation { id: string; state: ConversationState; last_message_at: Date; resolved_at: Date | null; subject: string | null; summary?: string | null; request_type?: string | null }

export function pickConversation(opts: {
  now: Date
  threadMatch: string | null               // conversation id found by In-Reply-To/References or chat session
  candidates: OpenConversation[]           // this person's conversations, newest first
  reopenDays: number
  idleNewAfterDays: number                 // an open conversation idle this long starts a new one
  /** The new message, for the topic check on resolved conversations (A 5.4-07). Without it, the window alone decides. */
  message?: { subject?: string | null; text: string }
  /** Told of each resolved conversation inside the window that was not reopened because the topic is different. */
  onNewTopic?: (conversationId: string) => void
}): { id: string; reopen: boolean; topic?: TopicMatch } | null {
  if (opts.threadMatch) {
    const c = opts.candidates.find((x) => x.id === opts.threadMatch)
    if (!c || c.state !== 'closed') return { id: opts.threadMatch, reopen: c?.state === 'resolved' }
  }
  for (const c of opts.candidates) {
    if (c.state === 'closed') continue
    if (c.state === 'resolved') {
      if (c.resolved_at && opts.now.getTime() - c.resolved_at.getTime() <= opts.reopenDays * 86_400_000) {
        const topic = opts.message ? topicMatch(opts.message, c) : 'unsure'
        if (topic === 'different') { opts.onNewTopic?.(c.id); continue }
        return { id: c.id, reopen: true, topic }
      }
      continue
    }
    if (opts.now.getTime() - c.last_message_at.getTime() <= opts.idleNewAfterDays * 86_400_000) return { id: c.id, reopen: false }
  }
  return null
}
